import { Chip } from "@mui/material";
import { STATUS_COLORS } from "../../theme";

/**
 * StatusBadge — Phase 2 reusable component.
 *
 * A thin wrapper around MUI's Chip that maps a semantic "tone" name to the
 * shared STATUS_COLORS palette added to theme.js in Phase 1, so every status
 * pill across the redesigned pages (position status, candidate feedback,
 * ticket priority, etc.) uses one consistent color source instead of each
 * page hardcoding its own hex values.
 *
 * This component does NOT decide what tone a given business status maps to
 * (e.g. "ACTIVE" -> ok, "CLOSED" -> neutral) — that mapping stays with each
 * page when it's redesigned in a later phase, since the correct mapping
 * depends on that page's existing business meaning and should be reviewed
 * per-page rather than guessed here.
 *
 * Props:
 *   label     - text shown in the chip (required)
 *   tone      - one of the STATUS_COLORS keys: "ok" | "warn" | "danger" |
 *               "info" | "violet" | "teal" | "neutral" (default: "neutral")
 *   size      - "small" | "medium" (default: "small")
 *   variant   - "soft" (tinted background, default) | "solid" (filled)
 *   sx        - optional additional MUI sx overrides, merged last
 */
export default function StatusBadge({
  label,
  tone = "neutral",
  size = "small",
  variant = "soft",
  sx = {},
  ...rest
}) {
  const palette = STATUS_COLORS[tone] || STATUS_COLORS.neutral;

  const baseStyles =
    variant === "solid"
      ? {
          bgcolor: palette.main,
          color: "#fff",
          border: "none",
        }
      : {
          bgcolor: palette.soft,
          color: palette.main,
          border: `1px solid ${palette.main}22`,
        };

  return (
    <Chip
      label={label}
      size={size}
      sx={{
        fontWeight: 700,
        letterSpacing: 0.2,
        ...baseStyles,
        ...sx,
      }}
      {...rest}
    />
  );
}
